/**
 * Protected Route Component
 * Restricts access to authenticated users only
 * Syncs auth with server when entering protected route
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@shared/contexts'
import { LoadingPage } from '@shared/components/feedback'

interface ProtectedRouteProps {
  children: ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, authChecked, refetchUser } = useAuth()
  const location = useLocation()
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncComplete, setSyncComplete] = useState(false)

  useEffect(() => {
    // Sync with server when entering protected route
    const syncAuth = async () => {
      if (isAuthenticated && !syncComplete) {
        setIsSyncing(true)
        const user = await refetchUser()
        setIsSyncing(false)
        setSyncComplete(true)

        // If sync failed, user will be redirected by the check below
        if (!user) {
          const currentPath = location.pathname + location.search
          if (currentPath !== '/' && !currentPath.includes('/login')) {
            localStorage.setItem('prevPathContact', currentPath)
          }
        }
      }
    }

    void syncAuth()
  }, [isAuthenticated, syncComplete, refetchUser, location])

  // Show loading while syncing
  if (isSyncing) {
    return <LoadingPage />
  }

  // Show loading while checking authentication (shouldn't happen often now)
  if (isLoading || !authChecked) {
    return <LoadingPage />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return <>{children}</>
}
